/**
 * usePracticeFactScores - Shared hook for tracking fact scores in practice screens
 *
 * Manages:
 * - Fact scores (correct/incorrect tracking)
 * - Completed count for progress display
 * - Retry queue for incorrect answers
 */

import { useState, useCallback, useRef, useEffect } from 'react';

export interface PracticeFactItem {
  factId: string;
  digit?: number;
  quantity?: number;
  value?: number;
}

interface UsePracticeFactScoresReturn {
  factScores: Map<string, number>;
  factScoresRef: React.MutableRefObject<Map<string, number>>;
  completedCount: number;
  failedQueue: PracticeFactItem[];
  isRetryPhase: boolean;
  currentRetryIndex: number;
  setCurrentRetryIndex: React.Dispatch<React.SetStateAction<number>>;
  updateScoreForCorrect: (factId: string) => { newScore: number; isFirstCorrect: boolean };
  updateScoreForIncorrect: (factId: string) => { newScore: number; wasCompleted: boolean };
  addToFailedQueue: (fact: PracticeFactItem) => void;
  startRetryPhase: () => void;
  isFactInFailedQueue: (factId: string) => boolean;
}

/**
 * Hook for managing fact scores and retry queue in practice screens
 */
export function usePracticeFactScores(): UsePracticeFactScoresReturn {
  const [factScores, setFactScores] = useState<Map<string, number>>(new Map());
  const factScoresRef = useRef(factScores);
  const [completedCount, setCompletedCount] = useState(0);
  const [failedQueue, setFailedQueue] = useState<PracticeFactItem[]>([]);
  const [isRetryPhase, setIsRetryPhase] = useState(false);
  const [currentRetryIndex, setCurrentRetryIndex] = useState(0);

  // Keep ref in sync with state
  useEffect(() => {
    factScoresRef.current = factScores;
  }, [factScores]);

  const updateScoreForCorrect = useCallback((factId: string) => {
    let newScore = 0;
    let isFirstCorrect = false;

    setFactScores((prev) => {
      const newScores = new Map(prev);
      const currentScore = newScores.get(factId) || 0;
      newScore = currentScore + 1;
      newScores.set(factId, newScore);

      // Only increment counter if going from 0 to 1 (first genuine correct)
      if (currentScore === 0 && newScore === 1) {
        isFirstCorrect = true;
        setCompletedCount((prevCount) => prevCount + 1);
      }

      return newScores;
    });

    return { newScore, isFirstCorrect };
  }, []);

  const updateScoreForIncorrect = useCallback((factId: string) => {
    let newScore = 0;
    let wasCompleted = false;

    setFactScores((prev) => {
      const newScores = new Map(prev);
      const currentScore = newScores.get(factId) || 0;
      newScore = currentScore - 1;
      newScores.set(factId, newScore);

      // If score goes below 1, decrement counter (was previously counted but now incorrect)
      if (currentScore >= 1 && newScore < 1) {
        wasCompleted = true;
        setCompletedCount((prevCount) => prevCount - 1);
      }

      return newScores;
    });

    return { newScore, wasCompleted };
  }, []);

  const addToFailedQueue = useCallback((fact: PracticeFactItem) => {
    setFailedQueue((prev) => {
      const alreadyExists = prev.some((f) => f.factId === fact.factId);
      if (!alreadyExists) {
        return [...prev, fact];
      }
      return prev;
    });
  }, []);

  const startRetryPhase = useCallback(() => {
    setIsRetryPhase(true);
    setCurrentRetryIndex(0);
  }, []);

  const isFactInFailedQueue = useCallback(
    (factId: string) => {
      return failedQueue.some((f) => f.factId === factId);
    },
    [failedQueue]
  );

  return {
    factScores,
    factScoresRef,
    completedCount,
    failedQueue,
    isRetryPhase,
    currentRetryIndex,
    setCurrentRetryIndex,
    updateScoreForCorrect,
    updateScoreForIncorrect,
    addToFailedQueue,
    startRetryPhase,
    isFactInFailedQueue,
  };
}

export default usePracticeFactScores;
